import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useActor, useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Heading,
  PageHeader,
} from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';
import { CustomField } from '@chargify/custom-fields';
import { useDeviceInfo } from '@chargify/device-provider';
import CustomFieldsForm from '../../components/CustomFieldsForm';
import { customFieldsSelectors } from '../../machines/customFields/selectors';
import { subscriptionsSelectors } from '../../machines';
import { GlobalStateContext } from '../../contexts';

const CustomFields = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['subscriptions-manager']);
  const { subscriptionsService } = useContext(GlobalStateContext);
  const { subscriptionId, catalogId } = useParams();
  const toastsService = useToastMessages();
  const { device } = useDeviceInfo();
  const isOnMobileDevice = ['tablet', 'mobile'].includes(device);
  const portalContainer = useRef<HTMLDivElement>(null);
  const { customFieldsRef, subscription } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(
        Number(catalogId),
        Number(subscriptionId)
      ),
      []
    )
  );

  const {
    product: { name },
  } = subscription;

  const [editedFields, setEditedFields] = useState<CustomField[]>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, sendToMachine] = useActor(customFieldsRef);
  const { isReady, isSaving, isSaveError } = useSelector(
    customFieldsRef,
    customFieldsSelectors.getSubscriptionCustomFieldsState
  );

  useEffect(() => {
    if (isReady) {
      toastsService.show({
        level: 'success',
        message: t('changes_saved', { ns: 'common' }),
      });
      navigate('/');
    }
  }, [isReady]);

  const customFields = useSelector(
    customFieldsRef,
    customFieldsSelectors.getSubscriptionCustomFields
  );

  const onFieldValueChange = (id: number, value: string | number) => {
    const fields = [...editedFields];
    const index = fields.findIndex((field) => {
      return field.id === id;
    });
    fields[index] = {
      ...fields[index],
      value: value as string,
    };
    setEditedFields(fields);
  };

  useEffect(() => {
    setEditedFields(customFields);
  }, [customFields]);

  return (
    <>
      <PageHeader title={name} size={isOnMobileDevice ? 'small' : 'medium'} />
      <Card>
        <Card.Header>
          <Heading variant="h3">
            {t('custom_fields.additional_settings', {
              ns: 'subscriptions-manager',
            })}
          </Heading>
        </Card.Header>
        <Card.Body>
          {editedFields && (
            <CustomFieldsForm
              portalContainer={portalContainer}
              customFields={editedFields}
              onChange={onFieldValueChange}
              isError={isSaveError}
            />
          )}
        </Card.Body>
        <Card.Footer>
          <Button.Group>
            <Button
              label={t('custom_fields.update_additional_settings')}
              color={'primary'}
              onClick={() => {
                sendToMachine({
                  type: 'SAVE',
                  payload: editedFields.map(({ id, value }) => ({
                    id,
                    value,
                  })),
                });
              }}
              isLoading={isSaving}
            />
            <Button
              label={t('cancel', { ns: 'common' })}
              color={'secondary'}
              onClick={() => navigate('/')}
            />
          </Button.Group>
        </Card.Footer>
      </Card>
      <div
        ref={portalContainer}
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
    </>
  );
};

export default CustomFields;
